import { createSelector } from "reselect";

import ChatMessageRole from "@mapmycustomers/shared/enum/chat/ChatMessageRole";
import ChatUserMessageStatus from "@mapmycustomers/shared/enum/chat/ChatUserMessageStatus";

import { RootState } from "@app/store/rootReducer";
import { ChatThreadKey } from "@app/util/chat/getChatThreadKey";

import { emptyChatThread } from "./ChatConversationState";

const chatConversationState = (state: RootState) => state.chat.conversation;

// Every read takes the key of the surface asking. Nothing derives it any more: the card in a
// record's pane and the floating window are on screen together, showing two conversations, so a
// single "active thread" could only ever be right for one of them.
const chatThread = (state: RootState, threadKey: ChatThreadKey) =>
  chatConversationState(state).byThread[threadKey] ?? emptyChatThread;

export const getChatThreadConversationId = (state: RootState, threadKey: ChatThreadKey) =>
  chatThread(state, threadKey).conversationId;

export const getChatThreadMessages = (state: RootState, threadKey: ChatThreadKey) =>
  chatThread(state, threadKey).messages;

export const getChatThreadSuggestions = (state: RootState, threadKey: ChatThreadKey) =>
  chatThread(state, threadKey).suggestions;

export const getChatProposalUi = createSelector(
  chatConversationState,
  ({ proposalUi }) => proposalUi
);

export const getChatProposalApply = createSelector(
  chatConversationState,
  ({ proposalApply }) => proposalApply
);

// Every thread: a turn that completed on a record the rep has since left still belongs in storage.
export const getChatThreads = createSelector(chatConversationState, ({ byThread }) => byThread);

// Derived rather than stored: a turn is in flight exactly while one of our own messages is
// still sending. A second source of truth here could disagree with the messages it describes.
// The whole thread is scanned, not just the newest message, because a retry puts a message
// that is not last back into SENDING — and a tail-only guard would leave the composer enabled
// for a second concurrent POST on a conversation BE has already locked for the turn. Per thread:
// a turn on a record must not disable the composer on the rep's own conversation, or the reverse.
export const isChatThreadTurnInProgress = (state: RootState, threadKey: ChatThreadKey) =>
  getChatThreadMessages(state, threadKey).some(
    (message) =>
      message.role === ChatMessageRole.USER && message.status === ChatUserMessageStatus.SENDING
  );
